/**
 * GoogleReviewsShowcase — Iter 67ab → 67ee.
 *
 * Home-page social-proof block. Was a flat 3-column grid of "best reviews
 * across hosts"; rebuilt as a **vendor-by-vendor carousel**: each slide
 * shows ONE host, their Google rating + review count, and up to **3** of
 * their best reviews. Auto-rotates every 8 s; pauses on hover; renter can
 * step manually with chevrons / dots / arrow keys. The country chip strip
 * is preserved — clicking a chip narrows the carousel to that country.
 *
 * Self-hides entirely when the integration isn't enabled (no API key) or
 * when no vendors have linked a Place ID yet.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import VendorNameLink from "@/components/common/VendorNameLink";
import PlatformGoogleRating from "@/components/home/PlatformGoogleRating";
import {
  Star,
  ExternalLink,
  Quote,
  Award,
  Globe2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const SLIDE_INTERVAL_MS = 8000;

export default function GoogleReviewsShowcase() {
  const [data, setData] = useState(null);
  const [featured, setFeatured] = useState(null);
  // Iter 75 — the platform's OWN Google Business Profile rating block.
  const [platform, setPlatform] = useState(null);
  const [loading, setLoading] = useState(true);
  // Iter 67cg — country filter chip ("All" when null).
  const [countryFilter, setCountryFilter] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false);
  // Iter 67ee — carousel slide index (per filtered vendor list).
  const [slideIndex, setSlideIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const slideTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/google-reviews/aggregate", { params: { limit: 9 } }).catch(() => null),
      api.get("/featured-host").catch(() => null),
      api.get("/google-reviews/platform").catch(() => null),
    ]).then(([reviewsRes, featuredRes, platformRes]) => {
      if (cancelled) return;
      setData(reviewsRes ? reviewsRes.data : null);
      setFeatured(featuredRes ? featuredRes.data : null);
      setPlatform(platformRes ? platformRes.data : null);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const vendorsAll = (data && data.vendors) || [];
  const countries = Array.from(new Set(vendorsAll.map((v) => v.country).filter(Boolean))).sort();
  const countryCounts = vendorsAll.reduce((acc, v) => {
    if (v.country) acc[v.country] = (acc[v.country] || 0) + 1;
    return acc;
  }, {});

  // Auto-rotate country chips (until renter touches anything).
  useEffect(() => {
    if (userInteracted || countries.length < 2) return undefined;
    const cycle = [null, ...countries];
    const t = setInterval(() => {
      setCountryFilter((cur) => {
        const idx = cycle.indexOf(cur);
        return cycle[(idx + 1) % cycle.length];
      });
    }, 6000);
    return () => clearInterval(t);
  }, [userInteracted, countries]);

  // When the filter changes, snap back to the first vendor.
  useEffect(() => { setSlideIndex(0); }, [countryFilter]);

  const visibleVendors = countryFilter
    ? vendorsAll.filter((v) => v.country === countryFilter)
    : vendorsAll;

  // Auto-advance vendor carousel.
  useEffect(() => {
    if (visibleVendors.length <= 1 || paused) return undefined;
    slideTimerRef.current = setInterval(() => {
      setSlideIndex((i) => (i + 1) % visibleVendors.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(slideTimerRef.current);
  }, [visibleVendors.length, paused]);

  // Keyboard navigation when the carousel is focused.
  const onKeyDown = (e) => {
    if (visibleVendors.length <= 1) return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setSlideIndex((i) => (i - 1 + visibleVendors.length) % visibleVendors.length);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setSlideIndex((i) => (i + 1) % visibleVendors.length);
    }
  };

  if (loading) return null;
  const reviewsEnabled = data && data.enabled;
  const featuredVendor = featured?.enabled ? featured.vendor : null;
  const platformVisible = !!(platform && platform.enabled && platform.configured && platform.rating);
  if (!featuredVendor && !platformVisible && (!reviewsEnabled || vendorsAll.length === 0)) return null;

  const ratingAvg = Number(data?.rating_avg || 0);
  const totalReviews = Number(data?.total_reviews || 0);
  const vendorCount = Number(data?.vendor_count || 0);
  const featuredId = featuredVendor?.vendor_id;

  const currentVendor = visibleVendors[Math.min(slideIndex, visibleVendors.length - 1)];

  const onSelectCountry = (c) => {
    setUserInteracted(true);
    setCountryFilter(c);
  };

  const goPrev = () => {
    setUserInteracted(true);
    setSlideIndex((i) => (i - 1 + visibleVendors.length) % visibleVendors.length);
  };
  const goNext = () => {
    setUserInteracted(true);
    setSlideIndex((i) => (i + 1) % visibleVendors.length);
  };

  return (
    <section className="bg-gradient-to-br from-neutral-950 via-indigo-950 to-sky-950 text-white py-20" data-testid="google-reviews-showcase">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-amber-300 text-[11px] font-bold uppercase tracking-[0.18em] mb-4 backdrop-blur-sm border border-white/10">
              <GoogleGlyph />
              Verified on Google
            </div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-semibold leading-[1.05] max-w-2xl" data-testid="google-reviews-headline">
              Trusted by travelers<br />
              <span className="text-white/60">across Europe</span>
            </h2>
          </div>

          <div className="flex items-center gap-8" data-testid="google-reviews-stats">
            {ratingAvg > 0 && (
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end">
                  <span className="font-display text-4xl sm:text-5xl font-semibold">{ratingAvg.toFixed(1)}</span>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`h-4 w-4 ${i < Math.round(ratingAvg) ? "fill-amber-400 text-amber-400" : "text-white/30"}`} />
                    ))}
                  </div>
                </div>
                <div className="text-xs text-white/60 mt-1 uppercase tracking-wider font-semibold">Average rating</div>
              </div>
            )}
            {totalReviews > 0 && (
              <div className="text-right">
                <div className="font-display text-4xl sm:text-5xl font-semibold">{totalReviews.toLocaleString()}</div>
                <div className="text-xs text-white/60 mt-1 uppercase tracking-wider font-semibold">Google reviews</div>
              </div>
            )}
            {vendorCount > 0 && (
              <div className="text-right hidden md:block">
                <div className="font-display text-4xl sm:text-5xl font-semibold">{vendorCount}</div>
                <div className="text-xs text-white/60 mt-1 uppercase tracking-wider font-semibold">Local hosts</div>
              </div>
            )}
          </div>
        </div>

        {/* Iter 75 — Platform's own Google Business rating, merged at the
            top of the Reviews area. */}
        {platformVisible && <PlatformGoogleRating data={platform} />}

        {featuredVendor && <FeaturedHostCard vendor={featuredVendor} weekIso={featured?.week_iso} />}

        {countries.length >= 2 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="google-reviews-country-chips">
            <span className="text-[11px] font-bold text-white/50 uppercase tracking-[0.18em] mr-1 flex items-center gap-1.5">
              <Globe2 className="h-3.5 w-3.5" /> By country
            </span>
            <button
              type="button"
              onClick={() => onSelectCountry(null)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition ${countryFilter === null ? "bg-white text-neutral-900" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
              data-testid="google-reviews-country-chip-all"
            >
              All <span className="ml-1 text-[10px] opacity-70 tabular-nums">{vendorsAll.length}</span>
            </button>
            {countries.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => onSelectCountry(c)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition ${countryFilter === c ? "bg-white text-neutral-900" : "bg-white/10 text-white/80 hover:bg-white/20"}`}
                data-testid={`google-reviews-country-chip-${c}`}
              >
                {c} <span className="ml-1 text-[10px] opacity-70 tabular-nums">{countryCounts[c] || 0}</span>
              </button>
            ))}
            {!userInteracted && (
              <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-white/40 font-semibold hidden sm:inline" data-testid="google-reviews-autorotate-indicator">
                auto-rotating
              </span>
            )}
          </div>
        )}

        {/* Iter 67ee — Vendor carousel. One slide per host, max 3 reviews. */}
        {reviewsEnabled && visibleVendors.length > 0 && currentVendor && (
          <div
            className="relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            tabIndex={0}
            onKeyDown={onKeyDown}
            data-testid="google-reviews-vendor-carousel"
            aria-roledescription="carousel"
            aria-label="Reviews by host"
          >
            <VendorReviewSlide
              vendor={currentVendor}
              isFeatured={currentVendor.vendor_id === featuredId}
            />

            {visibleVendors.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label="Previous host"
                  className="hidden md:flex absolute -left-3 lg:-left-5 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white items-center justify-center ring-1 ring-white/20 transition"
                  data-testid="google-reviews-carousel-prev"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label="Next host"
                  className="hidden md:flex absolute -right-3 lg:-right-5 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white items-center justify-center ring-1 ring-white/20 transition"
                  data-testid="google-reviews-carousel-next"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>

                <div
                  className="flex items-center justify-center gap-2 mt-6"
                  data-testid="google-reviews-carousel-dots"
                >
                  {/* Mobile fallback: prev / next inline since the side chevrons are hidden on small screens. */}
                  <button
                    type="button"
                    onClick={goPrev}
                    aria-label="Previous host"
                    className="md:hidden h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center ring-1 ring-white/20"
                    data-testid="google-reviews-carousel-prev-mobile"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  {visibleVendors.map((v, i) => (
                    <button
                      key={v.vendor_id}
                      type="button"
                      onClick={() => { setUserInteracted(true); setSlideIndex(i); }}
                      aria-label={`Show reviews from ${v.vendor_name}`}
                      className={`h-1.5 rounded-full transition-all ${i === slideIndex ? "w-8 bg-amber-400" : "w-1.5 bg-white/30 hover:bg-white/60"}`}
                      data-testid={`google-reviews-carousel-dot-${i}`}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={goNext}
                    aria-label="Next host"
                    className="md:hidden h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center ring-1 ring-white/20"
                    data-testid="google-reviews-carousel-next-mobile"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {reviewsEnabled && visibleVendors.length === 0 && countryFilter && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-white/70" data-testid="google-reviews-empty-country">
            No hosts with Google reviews from <strong>{countryFilter}</strong> yet.{" "}
            <button onClick={() => onSelectCountry(null)} className="underline">See all</button>
          </div>
        )}
      </div>
    </section>
  );
}

function FeaturedHostCard({ vendor, weekIso }) {
  const stars = Math.round(Number(vendor.rating) || 0);
  return (
    <div
      className="mb-8 rounded-3xl border border-amber-300/40 bg-gradient-to-br from-amber-500/10 via-amber-400/5 to-transparent backdrop-blur-sm p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6"
      data-testid="featured-host-card"
    >
      <div className="flex items-center gap-4 min-w-0 flex-1">
        {vendor.vendor_logo ? (
          <img
            src={fileUrl(vendor.vendor_logo)}
            alt={vendor.vendor_name}
            className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl object-contain bg-white p-2 shrink-0 ring-2 ring-amber-300/40"
            data-testid="featured-host-logo"
          />
        ) : (
          <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-2xl font-display font-semibold text-white shrink-0 ring-2 ring-amber-300/40">
            {(vendor.vendor_name || "?").trim()[0]?.toUpperCase() || "?"}
          </div>
        )}
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[10px] font-bold uppercase tracking-[0.18em] mb-2" data-testid="featured-host-badge">
            <Award className="h-3 w-3" /> Featured host of the week
          </div>
          <div className="font-display text-2xl sm:text-3xl font-semibold text-white truncate" data-testid="featured-host-name">
            <VendorNameLink
              vendor={{ business_name: vendor.vendor_name, public_slug: vendor.vendor_slug }}
              className="text-white hover:underline"
              testIdSuffix="featured-host"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-white/70 mt-1">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className={`h-3.5 w-3.5 ${i < stars ? "fill-amber-400 text-amber-400" : "text-white/20"}`} />
              ))}
            </div>
            <span className="font-semibold text-white">{Number(vendor.rating).toFixed(1)}</span>
            <span>· {Number(vendor.review_count).toLocaleString()} reviews</span>
            {weekIso && <span className="hidden sm:inline opacity-50">· {weekIso}</span>}
          </div>
        </div>
      </div>

      <Link
        to={`/cars?vendor_id=${encodeURIComponent(vendor.vendor_id)}`}
        className="shrink-0 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-amber-950 font-semibold text-sm transition shadow-lg shadow-amber-500/20"
        data-testid="featured-host-cta"
      >
        Browse this host's fleet
        <ExternalLink className="h-4 w-4" />
      </Link>
    </div>
  );
}

// Iter 67ee — One carousel slide: vendor header + up to 3 reviews.
function VendorReviewSlide({ vendor, isFeatured = false }) {
  const stars = Math.round(Number(vendor.rating) || 0);
  return (
    <div
      className={`rounded-3xl border bg-white/[0.04] backdrop-blur-sm p-6 sm:p-8 transition ${
        isFeatured
          ? "border-amber-400/50 ring-1 ring-amber-400/20"
          : "border-white/10"
      }`}
      data-testid={`google-reviews-vendor-slide-${vendor.vendor_id}`}
    >
      {/* Vendor header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-5 mb-6">
        <div className="flex items-center gap-4 min-w-0">
          {vendor.vendor_logo ? (
            <img
              src={fileUrl(vendor.vendor_logo)}
              alt={vendor.vendor_name}
              className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl object-contain bg-white p-1.5 shrink-0 ring-1 ring-white/20"
              data-testid="google-reviews-vendor-logo"
            />
          ) : (
            <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 flex items-center justify-center text-xl font-display font-semibold text-white shrink-0 ring-1 ring-amber-200/30">
              {(vendor.vendor_name || "?").trim()[0]?.toUpperCase() || "?"}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display text-xl sm:text-2xl font-semibold text-white truncate" data-testid="google-reviews-vendor-name">
                <VendorNameLink
                  vendor={{ business_name: vendor.vendor_name, public_slug: vendor.vendor_slug }}
                  className="text-white hover:underline"
                  testIdSuffix={`gr-slide-${vendor.vendor_id}`}
                />
              </div>
              {isFeatured && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400 text-amber-950 text-[9px] font-bold uppercase tracking-[0.14em]">
                  <Award className="h-2.5 w-2.5" /> Featured
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-sm text-white/70 mt-1 flex-wrap">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`h-3.5 w-3.5 ${i < stars ? "fill-amber-400 text-amber-400" : "text-white/20"}`} />
                ))}
              </div>
              {vendor.rating != null && (
                <span className="font-semibold text-white">{Number(vendor.rating).toFixed(1)}</span>
              )}
              {vendor.review_count > 0 && (
                <span>· {Number(vendor.review_count).toLocaleString()} reviews</span>
              )}
              {vendor.country && (
                <span className="hidden sm:inline opacity-70">· {vendor.country}</span>
              )}
            </div>
          </div>
        </div>

        <Link
          to={`/cars?vendor_id=${encodeURIComponent(vendor.vendor_id)}`}
          className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-neutral-900 hover:bg-amber-300 font-semibold text-sm transition"
          data-testid="google-reviews-vendor-cta"
        >
          Browse this host's fleet
          <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Up to 3 reviews — responsive grid: 1 col on mobile, 3 cols ≥ md. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(vendor.reviews || []).slice(0, 3).map((r, i) => (
          <ReviewMiniTile key={`${vendor.vendor_id}-${i}`} review={r} index={i} />
        ))}
      </div>
    </div>
  );
}

function ReviewMiniTile({ review, index }) {
  const stars = Math.round(Number(review.rating) || 0);
  return (
    <div
      className="relative rounded-2xl bg-white/[0.05] border border-white/10 p-5 hover:bg-white/[0.09] transition"
      data-testid={`google-reviews-vendor-review-${index}`}
    >
      <Quote className="absolute top-4 right-4 h-5 w-5 text-amber-400/30" />
      <div className="flex items-center gap-0.5 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className={`h-3.5 w-3.5 ${i < stars ? "fill-amber-400 text-amber-400" : "text-white/20"}`} />
        ))}
      </div>
      <p className="text-[13px] leading-relaxed text-white/85 line-clamp-6 mb-4">
        “{review.text}”
      </p>
      <div className="pt-3 border-t border-white/10 text-xs text-white/70">
        <div className="font-medium text-white truncate">{review.author || "Google reviewer"}</div>
        {review.relative_time && (
          <div className="text-white/50 mt-0.5 text-[11px]">{review.relative_time}</div>
        )}
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12s5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24s8.955,20,20,20s20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"/>
      <path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"/>
      <path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"/>
      <path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"/>
    </svg>
  );
}
